import classNames from 'classnames'
import moment from 'moment-timezone'
import React from 'react'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { DateTimePickerPopover } from 'browser/components/atomic-elements/atoms/date-picker/date-time-picker-popover'
import { useUI2Platform } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import { formatDateTimeValue } from 'shared-libs/components/ui2/fields/ui2-date-time-field/ui2-date-time-field-props'
import { DateTimeBottomSheet } from './date-time-bottom-sheet'
import './_date-time-input.scss'

const MODE_PLACEHOLDERS: Record<string, string> = {
  date: 'Select date',
  datetime: 'Select date and time',
  time: 'Select time',
}

export interface IDateTimeInputProps extends IBaseProps {
  value?: any
  onChange: (value: any) => void
  mode?: 'date' | 'time' | 'datetime'
  valueMode?: 'date' | 'time' | 'datetime'
  isDisabled?: boolean
  placeholder?: string
  showTimezoneInput?: boolean
  minDate?: string
  maxDate?: string
  daySize?: number
  minuteStep?: number
  defaultTimezone?: string
  isHorizontalLayout?: boolean
  rightAddon?: React.ReactNode
  /** Renders an inline clear affordance while a value is present. */
  isClearable?: boolean
}

/**
 * Clickable trigger that opens a combined date + time picker.
 * Supports date-only, time-only, and datetime modes.
 *
 * Presentation differs by platform (read from FormFieldPlatformContext):
 *   - desktopWeb: tethered popover anchored to the input
 *   - mobileWeb: full-width bottom sheet
 *
 * In both modes the value is only committed when the user presses Apply
 * inside the picker.
 */
export const DateTimeInput: React.FC<IDateTimeInputProps> = (props) => {
  const platform = useUI2Platform()
  const {
    value, onChange, isDisabled, placeholder,
    showTimezoneInput, minDate, maxDate, daySize, defaultTimezone = moment.tz.guess(),
    mode = 'datetime', valueMode, minuteStep = 5, rightAddon, isClearable,
  } = props
  const [isSheetOpen, setSheetOpen] = React.useState(false)

  // One shared formatter across NM / DW / MW, so a timezone-aware value reads
  // the same everywhere (rendered in its stored zone, suffixed with it).
  const formattedValue = formatDateTimeValue(value, mode, showTimezoneInput)
  const effectivePlaceholder = placeholder ?? MODE_PLACEHOLDERS[mode]
  const icon = mode === 'time' ? IconNames.TIME : IconNames.CALENDAR
  const showClear = !!isClearable && !!formattedValue && !isDisabled

  // Stop the click from reaching the trigger, or clearing would immediately
  // reopen the picker it just cleared.
  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    onChange(null)
  }

  const trigger = (
    <div
      className={classNames('c-dtInput', {
        'c-dtInput--isDisabled': isDisabled,
      })}
      onClick={platform === 'mobileWeb' && !isDisabled ? () => setSheetOpen(true) : undefined}
    >
      <span
        className={classNames('c-dtInput-value', {
          'c-dtInput-value--placeholder': !formattedValue,
        })}
      >
        {formattedValue || effectivePlaceholder}
      </span>
      {rightAddon && (
        <span className='c-dtInput-rightAddon'>{rightAddon}</span>
      )}
      {showClear && (
        <button
          type='button'
          className='c-dtInput-clear'
          onClick={handleClear}
          aria-label='Clear value'
        >
          <Icon icon={IconNames.CROSS} iconSize={12} />
        </button>
      )}
      <span className='c-dtInput-icon c-fieldControl-staticBadge c-fieldControl-staticBadge--inline'>
        <Icon icon={icon} />
      </span>
    </div>
  )

  const pickerProps = {
    value,
    onChange,
    mode,
    valueMode,
    showTimezoneInput,
    minDate,
    maxDate,
    daySize,
    minuteStep,
    defaultTimezone,
  }

  if (platform === 'mobileWeb') {
    return (
      <>
        {trigger}
        <DateTimeBottomSheet
          isOpen={isSheetOpen}
          onClose={() => setSheetOpen(false)}
          title={effectivePlaceholder}
          pickerProps={pickerProps}
        />
      </>
    )
  }

  return (
    <TetherTarget openOnClick={!isDisabled} closeOnEsc={true} tethered={<DateTimePickerPopover {...pickerProps} />}>
      {trigger}
    </TetherTarget>
  )
}
